import { FC, useMemo } from 'react';

import { Box, Stack, Typography } from '@mui/material';
import { Formik } from 'formik';

import { useParams } from '@redwoodjs/router';
import { MetaTags, useQuery } from '@redwoodjs/web';

import { Accordian, FieldInput } from 'src/components';
import { Flex } from 'src/components/Flex';
import Loader from 'src/components/Loader/Loader';
import { showErrorToast } from 'src/components/Toasts/toast';
import { DEFAULT_BORDER } from 'src/constants';
import { GET_TRANSACTION_PRICING_OFFER } from 'src/graphql/queries/getTransactionPricingOffer.query';
import {
    AdditionalTariffs,
    CompliancePricing,
    TransactionPricing,
} from 'src/pages/AddTransactionPricingPage/sections';

const ViewTransactionPricingPage: FC = () => {
    const { id } = useParams();
    const { data, loading } = useQuery(GET_TRANSACTION_PRICING_OFFER, {
        onError: (error) => {
            return showErrorToast(error.message);
        },
        variables: { transactionOfferId: id },
    });

    const initialValues = useMemo(() => {
        if (data?.getTransactionPricing) {
            const {
                additionalTariffCharges,
                compliancePricingCharges,
                transactionPricingCards,
                offerName,
                revenueShare,
            } = data?.getTransactionPricing;

            const transactionPricing = {
                personalCards: {},
                commercialCards: {},
                otherCards: {},
                locality: {},
            };
            const additionalTariffs = {
                personalCards: {},
            };

            const compliancePricing = {
                worldpayProgramme: {},
                safePayments: {},
            };

            transactionPricingCards.forEach(({ id, name, cardCategory, ...rest }) => {
                const newObj = { ...rest, isViewState: true };
                if (cardCategory === 'PERSONAL_CARDS') {
                    transactionPricing.personalCards[name] = newObj;
                } else if (cardCategory === 'COMMERCIAL_CARDS') {
                    transactionPricing.commercialCards[name] = newObj;
                } else if (cardCategory === 'OTHER') {
                    transactionPricing.otherCards[name] = newObj;
                }
            });

            additionalTariffCharges.forEach(({ id, name, type, ...rest }) => {
                const newObj = { ...rest, isViewState: true, type };
                if (type === 'PER_ITEM') {
                    additionalTariffs.personalCards[name] = newObj;
                } else if (type === 'FIXED') {
                    additionalTariffs.personalCards[name] = newObj;
                }
            });

            compliancePricingCharges.forEach(({ id, name, type, chargeType, ...rest }) => {
                const newObj = { ...rest, isViewState: true, type };

                if (chargeType === 'WORLDPAY_PROGRAMME') {
                    compliancePricing.worldpayProgramme[name] = newObj;
                } else if (chargeType === 'SAFE_PAYMENTS') {
                    compliancePricing.safePayments[name] = newObj;
                }
            });

            return {
                offerName,
                revenueShare,
                transactionPricing,
                additionalTariffs,
                compliancePricing,
            };
        }
    }, [data]);

    return (
        <>
            <MetaTags title="View Transaction Pricing" description="Transaction pricing" />
            {loading ? (
                <Loader />
            ) : (
                <Formik initialValues={initialValues} enableReinitialize onSubmit={() => {}}>
                    {({ values }) => {
                        return (
                            <Stack spacing={5}>
                                <Box
                                    sx={{
                                        ...DEFAULT_BORDER,
                                        padding: '32px 24px',
                                        boxShadow: '0px 4px 20px 0px #2632381A',
                                    }}
                                >
                                    <Typography
                                        fontSize="24px"
                                        lineHeight="34px"
                                        fontWeight="700"
                                        color="#1d1d1b"
                                    >
                                        Transaction Pricing Offers
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        ...DEFAULT_BORDER,
                                        boxShadow: '0px 4px 20px 0px #2632381A',
                                    }}
                                >
                                    <Box
                                        padding="16px"
                                        bgcolor="#F39E4E"
                                        borderRadius="16px 16px 0px 0px"
                                    >
                                        <Typography
                                            fontSize="20px"
                                            fontWeight="500"
                                            lineHeight="24px"
                                            color="#fff"
                                        >
                                            Add Transaction Pricing Offers
                                        </Typography>
                                    </Box>
                                    <Flex flexDirection="column" padding="24px" gap="24px">
                                        <Flex gap="24px">
                                            <FieldInput
                                                name="offerName"
                                                label="Pricing Offer Name"
                                                disabled
                                            />
                                            <FieldInput
                                                name="revenueShare"
                                                label="Revenue Share (%)"
                                                placeholder="Revenue Share"
                                                disabled
                                            />
                                        </Flex>

                                        <Accordian heading="Transaction Pricing">
                                            <TransactionPricing
                                                name="transactionPricing"
                                                notAdminUser
                                            />
                                        </Accordian>

                                        <Accordian heading="Additional Tariffs Table">
                                            <AdditionalTariffs
                                                name="additionalTariffs"
                                                notAdminUser
                                            />
                                        </Accordian>

                                        <Accordian heading="PCI Compliance Pricing">
                                            <CompliancePricing
                                                name="compliancePricing"
                                                notAdminUser
                                            />
                                        </Accordian>
                                    </Flex>
                                </Box>
                            </Stack>
                        );
                    }}
                </Formik>
            )}
        </>
    );
};

export default ViewTransactionPricingPage;
